/**
 * TenantLayout — shell panelu tenantowego (F7b T7b.8).
 *
 * Wariant admin `Layout.tsx` dla tenant scope:
 *  - Sidebar (host-aware z T7b.7) + TenantTopbar + main scroll.
 *  - Świadomy kopiarz struktury admin Layout — admin Topbar woła `useAuth()`, który
 *    rzuca w tenant scope (brak AuthProvider). Sidebar jest dzielony (host-aware).
 *
 * Używany przez tenant routes w `App.tsx` (TenantApp): Dashboard, MyTenants,
 * Settings, MyProfile, Tenant2FASetup.
 */

import type { ReactNode } from 'react';

import { BugReportWidget } from '@/components/bug-report/BugReportWidget';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ModuleUpdatesNotice } from '@/components/ModuleUpdatesNotice';
import { Sidebar } from '@/components/Sidebar';
import { TabBar } from '@/components/tabs/TabBar';
import { BugReportProvider } from '@/contexts/BugReportContext';
import { useTenantAuth } from '@/contexts/TenantAuthContext';

import { ImpersonationBanner } from './ImpersonationBanner';
import { TenantTopbar } from './TenantTopbar';

interface TenantLayoutProps {
  children: ReactNode;
}

export function TenantLayout({ children }: TenantLayoutProps) {
  // Uwaga: nie renderujemy `<main>` tutaj — każda strona (TenantDashboard,
  // MyTenants, Settings, MyProfile, Tenant2FASetup) dostarcza własny `<main>`
  // w obrębie swojego contentu. Dzięki temu nie łamiemy istniejących stron
  // (MyProfile / Tenant2FASetup z T7b.4/T7b.5) i zachowujemy spójność.
  const { isImpersonation, impersonatingAdminEmail, impersonationExpiresAt, endImpersonation } =
    useTenantAuth();

  // Stały-wysokościowy shell (h-screen + overflow-hidden): sidebar/topbar/pasek kart są
  // przyklejone, a przewijają się tylko panele kart (TenantTabPanels renderuje je jako
  // `absolute inset-0 overflow-auto`, więc obszar treści musi mieć określoną wysokość).
  return (
    <BugReportProvider>
      <div
        className="tenant-shell-bg relative flex h-screen overflow-hidden"
        data-testid="tenant-layout"
      >
        {/* Górny pasek akcentu USUNIĘTY (decyzja Eugenii 2026-09-16) — jednolity, neutralny wygląd. */}
        <Sidebar />
        {/* „Main column" wg Figmy (123:24680): kolumna z paddingiem 16 i odstępem 16 między
          sekcjami. Górny pasek i zakładki są FLUSH na jasnym tle strony (bez obramowanej tafli);
          treść (KPI + tabela) to osobne białe karty na tym tle. */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden p-4">
          <ImpersonationBanner
            isImpersonation={isImpersonation}
            adminEmail={impersonatingAdminEmail}
            expiresAt={impersonationExpiresAt}
            onEnd={endImpersonation}
          />
          <TenantTopbar />
          {/* Pasek kart (zakładek) — pod topbarem, nad treścią. */}
          <TabBar />
          {/* Powiadomienie o dostępnych aktualizacjach modułów z szablonu — tenant scope. */}
          <ModuleUpdatesNotice />
          {/* Per-content ErrorBoundary — sidebar/topbar/pasek kart zostają, pada tylko content.
            `data-tenant-content` = przewijalny kontener treści (tło strony; białe karty w środku). */}
          <div className="relative min-h-0 min-w-0 flex-1 overflow-auto" data-tenant-content>
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>
        </div>
        {/* Pływający widget „Zgłoś błąd" — portal do body, poza layoutem (nie blokuje treści). */}
        <BugReportWidget />
      </div>
    </BugReportProvider>
  );
}
